import { DAYS } from "../../utils/constantValues";
import { dayKeyOf, restaurantDayOf, restaurantToday, wallParts, zonedWallTimeToUtc } from "../../utils/restaurantClock";

// Slot-generation helpers for the checkout's "when" controls. Lifted verbatim
// out of the old Order Options dialog so the inline controls and Checkout's own
// open/closed check share one implementation instead of three.

// Scheduled times are offered every 15 minutes, after the lead time (prep
// time for pickup, 30 minutes for delivery).
export const SLOT_STEP_MINUTES = 15;

// Open windows (minutes-from-midnight) for a given day, from openingTimes.
// Shape: [{ day:'MON', times:[{ startTime:[H,M], endTime:[H,M] }] }]
export function openWindowsForDay(day, openingTimes) {
  if (!openingTimes || openingTimes.length === 0) return null; // unknown → no restriction
  const entry = openingTimes.find((o) => o.day === DAYS[day.getDay()]);
  if (!entry || !entry.times || entry.times.length === 0) return []; // closed all day
  return entry.times.map((t) => ({
    start: Number(t.startTime[0]) * 60 + Number(t.startTime[1]),
    end: Number(t.endTime[0]) * 60 + Number(t.endTime[1]),
  }));
}

// Generate time slots for a given day, limited to the restaurant's open hours
// and to >= now + leadMinutes (today). The first slot is anchored to the actual
// earliest fulfillable time — open + lead, or (today) now + lead — instead of a
// fixed clock grid, so e.g. a 12:00 PM open with a 15-min lead first offers
// 12:15 PM. Slots then step by stepMinutes (the prep time for pickup, 30 for
// delivery). The lead also buffers the close (last slot = close - lead).
// `notBefore` (a Date, optional) drops every slot before it — a catering
// order's notice — and starts that day's slots from it.
//
// `tz` is the restaurant's time zone (utils/restaurantClock.js): `day` is its
// calendar day and the hours are read on its clock, so a customer in another
// zone is offered the restaurant's 2:00 PM, not their own. Without it, this
// browser's clock.
export function generateTimeSlots(day, leadMinutes = 0, openingTimes, stepMinutes = 30, notBefore = null, tz = null) {
  const slots = [];
  const step = Math.max(5, Number(stepMinutes) || 30);
  const now = new Date();
  const dayKey = dayKeyOf(day);
  const isToday = dayKey === wallParts(now, tz).dateStr;
  const slotAt = (mins) => zonedWallTimeToUtc(dayKey, mins, tz);
  const floor = notBefore ? new Date(notBefore) : null;
  if (floor) {
    const dayEnd = new Date(slotAt(23 * 60 + 59).getTime() + 59999);
    if (dayEnd < floor) return []; // the whole day is inside the notice
  }

  const rawWindows = openWindowsForDay(day, openingTimes);
  if (rawWindows && rawWindows.length === 0) return []; // closed this whole day
  // Earliest/latest fulfillable minute within each open window after the lead.
  const windows = rawWindows
    ? rawWindows
        .map((w) => ({ start: w.start + leadMinutes, end: w.end - leadMinutes }))
        .filter((w) => w.start <= w.end)
    : null;
  if (windows && windows.length === 0) return []; // no window long enough for the lead

  // Anchor (minutes-from-midnight) for the very first slot of the day.
  let anchorMins;
  if (isToday) {
    const earliest = wallParts(new Date(now.getTime() + leadMinutes * 60000), tz);
    anchorMins = earliest.minutes + (earliest.seconds > 0 ? 1 : 0);
  } else {
    anchorMins = 0;
  }
  if (floor) {
    const f = wallParts(floor, tz);
    if (f.dateStr === dayKey) anchorMins = Math.max(anchorMins, f.minutes + (f.seconds > 0 ? 1 : 0));
  }
  // If the restaurant has open windows, start at the first open-window start that
  // is >= the today anchor (so closed early-morning hours are skipped entirely).
  if (windows) {
    const firstWindowStart = Math.min(...windows.map((w) => w.start));
    anchorMins = Math.max(anchorMins, firstWindowStart);
  }

  const endOfDayMins = 23 * 60 + 59;
  for (let m = anchorMins; m <= endOfDayMins; m += step) {
    if (windows && !windows.some((w) => m >= w.start && m <= w.end)) continue;
    const slot = slotAt(m);
    if (isToday && slot < now) continue;
    if (floor && slot < floor) continue;
    slots.push(slot);
  }
  return slots;
}

// True if the restaurant is currently within an open window right now, on its clock.
export function isOpenNow(openingTimes, tz = null) {
  if (!openingTimes || openingTimes.length === 0) return false;
  const now = new Date();
  const windows = openWindowsForDay(restaurantDayOf(now, tz), openingTimes);
  if (!windows || windows.length === 0) return false;
  const nowMins = wallParts(now, tz).minutes;
  return windows.some((w) => nowMins >= w.start && nowMins <= w.end);
}

// Generate the next `count` days, today first (7 = today + 6) — the
// restaurant's days when `tz` is given.
export function generateDays(count = 7, tz = null) {
  const today = restaurantToday(tz);
  const days = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    d.setHours(0, 0, 0, 0);
    days.push(d);
  }
  return days;
}

// Two Dates refer to the same slot when they match to the minute. The old dialog
// compared getTime() exactly, so a date arriving from the header — which carries
// seconds and milliseconds — never matched any generated slot and nothing ever
// showed as selected.
export function isSameSlot(a, b) {
  if (!a || !b) return false;
  return Math.floor(new Date(a).getTime() / 60000) === Math.floor(new Date(b).getTime() / 60000);
}
